import { inject, Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { AsyncEventEmitter } from './event/async-event-emitter';
import { ObserverCallback, ObserverConfig } from './event/observer';
/**
 * Event service usage
 *
 * Broadcasting
 *
 * 1. Define event interfaces
 *
 * import { DfEventConfig } from '@df/core/service/df-event';
 *
 * declare module '@df/core/service/df-event' {
 *      interface IEventType {
 *          someEventId: DfEventConfig<'someEventId', { ... data object interface ... }, { ... result data interface ... }>;
 *      }
 * }
 *
 * 2. Dispatch event
 *
 * Keep in mind that action may exist or not. It may be async or not.
 *
 * this.dfEvent.dispatch({
 *   id: 'someEventId',
 *   data: { ... data object ... },
 *   async action: (data) => {
 *      // do something
 *     return { ... result object ... };
 *    }
 * });
 *
 * 3. Capture event
 *
 * this.dfEvent.before(event => {
 *    if(event.id === 'someEventId') {
 *         console.log(event.data)
 *    }
 * })
 *
 * - You may stop processing by setting event.stopped = true;
 * - You may call event.action manually
 * - You may call event before, on success or on error
 * - You may kill the action only by making it undefined in event
 */

type DfEventAction<TData, TResult> = (event: TData) => TResult | Promise<TResult>;

export interface DfEventConfig<TId, TData, TResult> {
    id: TId;
    data: TData;
    result?: TResult;
    error?: string;
    stopped?: boolean;
    action?: DfEventAction<TData, TResult>;
}

export interface IEventType {}

export type DfEventKey = keyof IEventType;
export type DfEventData = IEventType[DfEventKey];

declare module '@df/core/app' {
    interface App {
        event: DfEventService;
    }
}

/**
 * Event service
 */
@Injectable({
    providedIn: 'root'
})
export class DfEventService {
    private before$ = new AsyncEventEmitter<DfEventData>();
    private success$ = new AsyncEventEmitter<DfEventData>();
    private error$ = new AsyncEventEmitter<DfEventData>();

    constructor() {
        inject(App).event = this;
    }

    /**
     * Subscrbe to before-action event
     */
    before(cb: ObserverCallback<DfEventData>, config?: ObserverConfig) {
        return this.before$.subscribe(cb, config);
    }

    /**
     * Subscribe to after-action event
     */
    success(cb: ObserverCallback<DfEventData>, config?: ObserverConfig) {
        return this.success$.subscribe(cb, config);
    }

    /**
     * Subscrbe to before-action event
     */
    error(cb: ObserverCallback<DfEventData>, config?: ObserverConfig) {
        return this.error$.subscribe(cb, config);
    }

    /**
     * Dispatch event
     */
    async dispatch<TResult>(event: DfEventData): Promise<TResult> {
        await this.before$.dispatch(event);
        if (!event.stopped) {
            if (event.action) {
                try {
                    const actionResult = event.action(event.data as never);
                    event.result = actionResult instanceof Promise ? await actionResult : actionResult;
                } catch (err) {
                    if (err instanceof Error) {
                        event.error = err.message;
                    } else if (typeof err === 'object' && err !== null && 'error' in err && typeof err.error === 'string') {
                        event.error = err.error;
                    } else if (typeof err === 'string') {
                        event.error = err;
                    } else if (typeof err === 'number') {
                        event.error = err.toString();
                    } else {
                        event.error = 'ERROR';
                    }
                    const error = event.error;
                    await this.error$.dispatch(event);
                    if (event.error) {
                        throw error === event.error ? err : { error: event.error };
                    }
                    if (event.stopped) {
                        return event.result as TResult;
                    }
                }
            }
            await this.success$.dispatch(event);
        }
        return event.result as TResult;
    }

    /**
     * Remove observer from the list
     */
    // unsubscribe(observer: Observer) {
    //     this.before$.unsubscribe(observer);
    //     this.success$.unsubscribe(observer);
    //     this.error$.unsubscribe(observer);
    // }
}
